import { Box, Button, Typography, Alert, Stack } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { login, getRateLimitInfo } from '../../services/auth'
import { useAuth } from '../../contexts/AuthContext'
import { useEffect, useState, useCallback } from 'react'
import { poolwerxColors } from '../../theme/colors'
import StandaloneCardLayout from '../../components/layouts/StandaloneCardLayout'
import { googleSignInButtonSx } from '../../components/auth/authButtonStyles'
import { GoogleIcon } from '../../components/SystemIcons'
import { Helmet } from 'react-helmet-async'
import DevEnvironmentBanner from '../../components/DevEnvironmentBanner'

export default function Login() {
  const { error, loading, isAuthenticated, isRateLimited, rateLimitResetTime, retryAuth } = useAuth()
  const [countdown, setCountdown] = useState<number>(0)
  const [isRetrying, setIsRetrying] = useState(false)

  // Update countdown when rate limited
  useEffect(() => {
    let interval: NodeJS.Timeout
    if (isRateLimited && rateLimitResetTime) {
      const updateCountdown = () => {
        const now = Date.now()
        const resetTime = rateLimitResetTime.getTime()
        const remaining = Math.max(0, Math.ceil((resetTime - now) / 1000))
        setCountdown(remaining)
        
        if (remaining <= 0) {
          setCountdown(0)
        }
      }
      
      updateCountdown()
      interval = setInterval(updateCountdown, 1000)
    } else if (isRateLimited) {
      // Use rate limit info for exponential backoff countdown
      const rateLimitInfo = getRateLimitInfo()
      if (rateLimitInfo.nextRetryIn > 0) {
        setCountdown(Math.ceil(rateLimitInfo.nextRetryIn / 1000))
        interval = setInterval(() => {
          setCountdown(prev => Math.max(0, prev - 1))
        }, 1000)
      }
    }
    
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isRateLimited, rateLimitResetTime])

  const handleLogin = () => {
    if (isRateLimited) {
      console.log('Login attempted while rate limited')
      return
    }
    
    const urlParams = new URLSearchParams(window.location.search)
    const redirectPath = urlParams.get('redirect') || '/'
    login(redirectPath)
  }

  const handleRetry = useCallback(async () => {
    if (isRetrying) return
    
    setIsRetrying(true)
    try {
      await retryAuth()
    } catch (error) {
      console.error('Manual retry failed:', error)
    } finally {
      setIsRetrying(false)
    }
  }, [retryAuth, isRetrying])

  // Auto-redirect already authenticated users
  useEffect(() => {
    if (isAuthenticated && !loading) {
      const urlParams = new URLSearchParams(window.location.search)
      const redirectPath = urlParams.get('redirect')
      
      // Validate redirect path - must start with / and not be login page
      let targetPath = '/'
      if (redirectPath && redirectPath.startsWith('/') && !redirectPath.startsWith('/login')) {
        targetPath = redirectPath
      }
      
      console.log('User already authenticated, redirecting to:', targetPath)
      window.location.href = targetPath
    }
  }, [isAuthenticated, loading])

  // Check for error from URL params (redirected from backend)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const errorParam = urlParams.get('error')
    if (errorParam) {
      console.error('Authentication error:', errorParam)
    }
  }, [])

  const urlParams = new URLSearchParams(window.location.search)
  const errorFromUrl = urlParams.get('error')
  const attemptedEmail = urlParams.get('email')
  const displayError = error || errorFromUrl

  // Show loading/redirect message for already authenticated users
  if (isAuthenticated && !loading) {
    return (
      <>
        <StandaloneCardLayout headerTitle="Redirecting..." showHeader={false}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" sx={{ color: poolwerxColors.teal, mb: 2 }}>
              🔄 Redirecting...
            </Typography>
            <Typography variant="body2" color="textSecondary">
              You're already signed in. Taking you to your destination.
            </Typography>
          </Box>
        </StandaloneCardLayout>
        <DevEnvironmentBanner />
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Login | Inspector Tool</title>
      </Helmet>
      <StandaloneCardLayout headerTitle="Login">
        <Stack spacing={3}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            Sign in with your Poolwerx Google account to continue
          </Typography>
        </Box>

        {isRateLimited && (
          <Alert 
            severity="warning" 
            action={
              countdown === 0 ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleRetry}
                  disabled={isRetrying}
                  startIcon={<RefreshIcon />}
                  sx={{ ml: 1 }}
                >
                  {isRetrying ? 'Retrying...' : 'Try Again'}
                </Button>
              ) : null
            }
          >
            <strong>⏳ Rate Limited</strong>
            <br />
            Too many authentication attempts. 
            {countdown > 0 ? (
              <> Please wait {countdown} seconds before trying again.</>
            ) : (
              <> You can now try again.</>
            )}
          </Alert>
        )}

        {displayError && !isRateLimited && (
          <Alert severity="error">
            {displayError === 'OAuth not configured. Please contact administrator.' && (
              <>
                <strong>🔧 Authentication System Not Configured</strong>
                <br />
                Please contact your system administrator to configure Google OAuth.
              </>
            )}
            {displayError === 'Access not authorized. Please contact support.' && (
              <>
                <strong>🚫 Access Not Authorized</strong>
                <br />
                Your account does not have access to this application. Please contact support.
              </>
            )}
            {displayError === 'unauthorized_domain' && (
              <>
                <strong>❌ Authentication Error</strong>
                <br />
                Access restricted. Use your Poolwerx Google account, or your invited partner account.
                {attemptedEmail && (
                  <>
                    <br />
                    Attempted with: <strong>{attemptedEmail}</strong>
                  </>
                )}
              </>
            )}
            {displayError === 'no_invitation' && (
              <>
                <strong>❌ Access Denied</strong>
                <br />
                Your account must be created by an administrator, or you must use a valid invitation link.
                {attemptedEmail && (
                  <>
                    <br />
                    Attempted with: <strong>{attemptedEmail}</strong>
                  </>
                )}
              </>
            )}
            {!displayError.includes('OAuth not configured') &&
             !displayError.includes('Access not authorized') &&
             displayError !== 'unauthorized_domain' &&
             displayError !== 'no_invitation' && (
              <>
                <strong>❌ Authentication Error</strong>
                <br />
                {displayError}
              </>
            )}
          </Alert>
        )}

        <Button
          variant="outlined"
          onClick={handleLogin}
          disabled={loading || isRateLimited}
          startIcon={<GoogleIcon />}
          sx={[googleSignInButtonSx, { width: '66.67%', alignSelf: 'center' }]}
        >
          {loading ? '⏳ Signing in...' :
           isRateLimited ? (countdown > 0 ? `⏳ Please wait ${countdown}s` : '⏳ Rate limited') :
           'Sign in with Google'}
        </Button>

      </Stack>
    </StandaloneCardLayout>
    <DevEnvironmentBanner />
    </>
  )
}